// @refresh reset
import type { MusicContextType } from '@tunasong/music-lib'
import { buildMusicContext } from '@tunasong/music-ui'
import type { EditorEvent, TunaEditor } from '@tunasong/plugin-lib'
import {
  RenderPlugins,
  TunaEditorContext,
  getEditorContainerId,
  registerLiveEditor,
  unregisterLiveEditor,
  useEditorMessage,
} from '@tunasong/plugin-lib'
import { useMusicContext } from '@tunasong/redux'
import type { CoreElement, TunaDecendant } from '@tunasong/schemas'
import { BrowserCapabilities, useDelayedInvoke } from '@tunasong/ui-lib'
import { Plate, PlateContent } from '@platejs/core/react'
import cn from 'classnames'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Node } from 'slate'
import invariant from 'tiny-invariant'
import { EditorMounted } from './element-editor-mount.js'
import styles from './element-editor.module.css'
import { RenderLeaf } from './render-leaf.js'
import { renderUnknownElement } from './render-unknown-element.js'
import { logger } from '@tunasong/models'

const noopScrollSelectionIntoView = () => {}

export interface ElementEditorProps<T extends CoreElement = CoreElement> {
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
  editor: TunaEditor
  element: T
  readOnly?: boolean
  autoFocus?: boolean

  /**
   * Placeholder text to show when the element is empty
   */
  placeholder?: string
  /** ready to render. Set to `false` if rendering should be postponed. @default true */
  ready?: boolean

  initialValue?: TunaDecendant[]

  /** onMounted: run when the editor is mounted and ready */
  onMounted?(): void
  /** onChange must ensure the updated element is set in element */
  onChange?(element: T): void
  /** Editor is considering the document complete - e.g., for a single-line editor */
  onComplete?(event: EditorEvent): void
  onContext?(context: MusicContextType): void
}

export const ElementEditor = <T extends CoreElement = CoreElement>(props: ElementEditorProps<T>) => {
  const {
    className,
    children,
    editor,
    element,
    initialValue,
    autoFocus,
    readOnly = false,
    ready = true,
    placeholder,
    onChange,
    onMounted,
    onComplete,
    onContext,
    style,
  } = props
  /** Messages that are handled by the plugin(s) */
  const showContextMenu = useEditorMessage({ editor, type: 'show-context-menu' })
  const onContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      const target = event.currentTarget instanceof HTMLElement ? event.currentTarget : null
      showContextMenu(element, target)
    },
    [element, showContextMenu]
  )

  /** Defer complete to allow onChange to finish */
  useEditorMessage({ editor, type: 'complete', handler: onComplete })

  /** Update context when editor and/element changes */
  const [, setContext, clearContext] = useMusicContext({ editorId: editor.id, selector: state => state })
  const [defaultContext] = useMusicContext({ editorId: 'default', selector: state => state })

  /** Initial context update on load */
  const initialized = useRef(false)

  useEffect(() => {
    if (!(editor && element && !initialized.current)) {
      return
    }
    initialized.current = true

    setContext(buildMusicContext(editor, element, defaultContext))
  }, [defaultContext, editor, element, setContext])

  // Evict this editor's music-context entry on unmount. The context is keyed by
  // the per-mount random `editor.id` and references document subtrees (chords,
  // sections), so without eviction every song switch leaked a document tree
  // into the `editors` slice forever → renderer OOM (GH #1628). Reset the
  // `initialized` latch too so a StrictMode mount→unmount→mount (dev) re-seeds
  // the context after the cleanup clears it.
  useEffect(
    () => () => {
      initialized.current = false
      clearContext()
    },
    [clearContext]
  )

  // Set `rootElement` synchronously during render so descendants that read
  // `editor.rootElement` during their own render (e.g.
  // `BlockDecorationsBar`'s `useEntityChildren({ parentId: songId, ... })`
  // call) see the correct value on first paint. The previous useEffect-only
  // assignment ran *after* the first render, leaving every block with
  // `parentId: undefined` until something else forced a re-render —
  // anchor markers were therefore missing for existing notes on initial load.
  // Mutating an object property during render is safe (no React state
  // involved); the useEffect below still runs as a safety net for any code
  // path that updates `element` after mount.
  if (editor.rootElement !== element) {
    editor.setProperty('rootElement', element)
  }
  useEffect(() => {
    editor.setProperty('rootElement', element)
    invariant(editor.rootElement === element)
  }, [element, editor])

  // Publish this editor to the live-editor registry so surfaces outside the
  // editor's React context (the agent sidebar's proposal panel) can resolve
  // it by entity id to apply accepted proposals. Keyed by the root element id.
  useEffect(() => {
    const id = element?.id
    if (!id) {
      return
    }
    registerLiveEditor(id, editor)
    return () => unregisterLiveEditor(id, editor)
  }, [element?.id, editor])

  /** Update the context after 250ms of no changes since that is potentially expensive */
  const delayedContext = useDelayedInvoke(250)

  const handleChange = useCallback(
    ({ editor, value: nodes }: { editor: TunaEditor; value: Node[] }) => {
      // Context depends on the selection
      delayedContext(() => {
        const editContext = buildMusicContext(editor, element)
        setContext(editContext)
        if (onContext) {
          onContext(editContext)
        }
      })

      /** if only selection has changed, do not call onChange */
      if (editor.operations.length > 0 && editor.operations.every(op => op.type === 'set_selection')) {
        return
      }

      if (!onChange) {
        return
      }
      const newElement: T = {
        ...element,
        children: nodes as TunaDecendant[],
      }

      onChange(newElement)
    },
    [delayedContext, element, onChange, onContext, setContext]
  )

  const hasValue = Boolean((initialValue ?? element?.children)?.length)
  const [focused, setFocused] = useState(false)

  const firstRender = useMemo(() => Date.now(), [])
  const handleMounted = useCallback(() => {
    const timeToMount = Date.now() - firstRender
    logger.debug(`Editor mounted in ${timeToMount}ms`, { editorId: editor.id, elementType: element?.type })
    if (onMounted) {
      onMounted()
    }
  }, [editor.id, element?.type, firstRender, onMounted])

  const onFocus = useCallback(() => {
    setFocused(true)
  }, [])
  const onBlur = useCallback(() => setFocused(false), [])

  if (!ready || !hasValue) {
    return null
  }

  return (
    <div
      id={getEditorContainerId(editor)}
      className={cn(styles.editorContainer, className)}
      // A name needs a role that permits one (axe aria-prohibited-attr); the label is also a test locator
      role="group"
      aria-label={`Editor for ${element?.type}`}
      style={style}
    >
      <RenderPlugins editor={editor} type="root" />
      <TunaEditorContext.Provider value={editor}>
        <RenderPlugins editor={editor} type="top" />

        <Plate
          key={editor.id}
          editor={editor as never}
          onValueChange={handleChange as never}
          renderLeaf={RenderLeaf}
          renderElement={renderUnknownElement(editor)}
          // data-autofocus is telling FocusLock to zero in on this
          data-autofocus
        >
          {onMounted ? <EditorMounted onMounted={handleMounted} /> : null}
          <PlateContent
            className={styles.plateContent}
            autoFocus={autoFocus}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={focused ? undefined : placeholder}
            // The wrapper's "Editor for <type>" names the group, not the textbox, so the textbox gets its own name
            aria-label={element?.type ? `${element.type[0].toUpperCase()}${element.type.slice(1)} content` : 'Content'}
            spellCheck={false}
            scrollSelectionIntoView={noopScrollSelectionIntoView}
            onContextMenu={onContextMenu}
            readOnly={readOnly || !BrowserCapabilities.supportsEdit}
          >
            {children}
          </PlateContent>
        </Plate>
      </TunaEditorContext.Provider>
    </div>
  )
}
